// Recruiting — a candidate's files and the "current resume" picker (owner,
// 2026-10-11; PROGRESS.md item E: "325 people have several resumes; Monday
// file dates are the 07/29/2026 migration date, not the resume's").
//
// The server decides which file is THE resume (src/recruiting.js
// resumeOrderSql): the one someone marked current, else the newest RESUME by
// a date it can trust. This file only shows that answer and offers "Make
// current" to anyone who may edit the candidate — the same rule the server
// enforces (owner/director any record; a manager their own clinic or a
// record with no clinic).
import type { JSX } from 'preact';
import { EmptyState } from '../FaultState';
import { useWrite } from '../MoneyForms';
import { formatCivilDate, type CandidateFile } from '../../lib/recruitingApi';
import { setCurrentResume } from '../../lib/recruitingWrite';
import type { RecruitingViewer } from './actions/constants';

/** Mirrors src/recruiting_write.js assertCanWriteCandidate — only decides
 * whether the button is worth drawing; the server decides the write. */
export function canEditCandidate(viewer: RecruitingViewer | null, homeLocationCode: string | null): boolean {
  if (!viewer) return false;
  if (viewer.role === 'OWNER' || viewer.role === 'DIRECTOR') return true;
  if (viewer.role === 'MANAGER') return homeLocationCode === null || homeLocationCode === viewer.locationCode;
  return false;
}

/** The file the page shows as the resume: the server's pick when it sent one,
 * else (an older cached response) the newest file — never a guess beyond
 * what the server already ordered. */
export function pickResume(files: CandidateFile[], resumeFileId: number | null | undefined): CandidateFile | null {
  if (resumeFileId !== undefined) {
    return resumeFileId === null ? null : (files.find((f) => f.id === resumeFileId) ?? null);
  }
  return files.find((f) => f.kind === 'RESUME') ?? files[0] ?? null;
}

function fileDateText(f: CandidateFile): string {
  if (f.resumeDate !== undefined) return f.resumeDate ? formatCivilDate(f.resumeDate) : 'date unknown';
  return f.fileDate ? formatCivilDate(f.fileDate) : '';
}

export function CandidateFiles(
  {
    candidateId, homeLocationCode, files, resumeFileId, currentResumeFileId, viewer, onChanged,
  }: {
    candidateId: number;
    homeLocationCode: string | null;
    files: CandidateFile[];
    resumeFileId: number | null | undefined;
    currentResumeFileId: number | null | undefined;
    viewer: RecruitingViewer | null;
    onChanged: () => void;
  },
): JSX.Element {
  const write = useWrite();
  const canEdit = canEditCandidate(viewer, homeLocationCode);
  const showPicker = canEdit && files.length > 1;
  const anyMondayDate = files.some((f) => f.mondayMigrationDate);

  function mark(fileId: number | null): void {
    void write.run(async () => { await setCurrentResume(candidateId, fileId); }, onChanged);
  }

  return (
    <section data-testid="recruiting-files">
      <h3>Files</h3>
      {files.length === 0 ? <EmptyState label="No files on file." /> : (
        <>
          <ul>
            {files.map((f) => {
              const isResume = resumeFileId !== undefined && f.id === resumeFileId;
              const dateText = fileDateText(f);
              return (
                <li key={f.id} data-testid={`recruiting-file-${f.id}`}>
                  {f.url ? (
                    <a class="doc-link" href={f.url} target="_blank" rel="noreferrer">{f.name}</a>
                  ) : (
                    <>{f.name} <span class="muted">in the Recruiting drive</span></>
                  )}
                  {dateText ? <span class={dateText === 'date unknown' ? 'muted' : undefined}>{` — ${dateText}`}</span> : null}
                  {isResume ? (
                    <span class={f.isCurrent ? 'badge ok' : 'badge'} data-testid={`recruiting-file-resume-${f.id}`}>
                      {f.isCurrent ? 'Current resume' : 'Shown as resume (newest)'}
                    </span>
                  ) : null}
                  {showPicker && !f.isCurrent ? (
                    <button
                      type="button" class="btn" disabled={write.busy}
                      onClick={() => mark(f.id)} data-testid={`make-current-${f.id}`}
                    >
                      Make current resume
                    </button>
                  ) : null}
                  {showPicker && f.isCurrent ? (
                    <button
                      type="button" class="btn" disabled={write.busy}
                      onClick={() => mark(null)} data-testid="clear-current-resume"
                    >
                      Use newest instead
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {showPicker || anyMondayDate ? (
            <p class="muted" data-testid="recruiting-files-hint">
              {showPicker && !currentResumeFileId
                ? 'No resume is marked current, so the newest is shown. '
                : ''}
              {anyMondayDate
                ? 'Files moved from Monday carry 07/29/2026 — the day they were moved, not the resume\'s date — so they show "date unknown". Open them to check which is newest.'
                : ''}
            </p>
          ) : null}
          {write.error ? <small class="bad" role="alert">{write.error}</small> : null}
        </>
      )}
    </section>
  );
}
